import React, { useMemo } from 'react';

import Icons from 'assets/icons';


import { Box } from 'ui/box';

import Button from 'components/partials/button/button';
import Modal from 'components/partials/modal/modal';
import TableComponent from 'components/partials/table-component/table-component';

import { getCompareEstimatesTableSettings } from './compare-estimates-table-settings';

import type { CompareEstimatesTableRow } from './compare-estimates-table-settings';
import type {
  ComparisonRow,
  ComparisonRowState,
  VersionOption,
} from '../hooks/use-compare-estimates';

const MIN_ROWS_FOR_DELETE = 1;

interface CompareEstimatesModalProps {
  currentRow: ComparisonRow;
  comparisonRowStates: ComparisonRowState[];
  versionOptions: VersionOption[];
  onSelectVersion: (rowId: string, versionId: number | undefined) => void;
  onAddRow: () => void;
  onRemoveRow: (rowId: string) => void;
  canAddRow: boolean;
  userId: string;
  onClose: () => void;
}

const CURRENT_ROW_ID = 'current';

const selectControlStyles = {
  minHeight: '28px',
  height: '28px',
  padding: 0,
  fontSize: '14px',
};

const SELECT_STYLES = (): Record<string, unknown> => ({
  valueContainer: (base: Record<string, unknown>) => ({
    ...base,
    height: '28px',
    padding: '0 10px',
  }),
  singleValue: (base: Record<string, unknown>) => ({
    ...base,
    position: 'static' as const,
    transform: 'none',
    maxWidth: '100%',
  }),
  input: (base: Record<string, unknown>) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  indicatorsContainer: (base: Record<string, unknown>) => ({
    ...base,
    height: '28px',
    alignItems: 'center',
  }),
  dropdownIndicator: (base: Record<string, unknown>) => ({
    ...base,
    padding: '0 6px',
  }),
  menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 }),
});

const CompareEstimatesModal = ({
  currentRow,
  comparisonRowStates,
  versionOptions,
  onSelectVersion,
  onAddRow,
  onRemoveRow,
  canAddRow,
  userId,
  onClose,
}: CompareEstimatesModalProps): React.JSX.Element => {
  const selectedVersionIds = useMemo(
    () =>
      new Set(
        comparisonRowStates
          .map((r) => r.selectedVersionId)
          .filter((id): id is number => id !== undefined)
      ),
    [comparisonRowStates]
  );

  const tableData: CompareEstimatesTableRow[] = useMemo(
    () => [
      {
        id: CURRENT_ROW_ID,
        isCurrent: true,
        versionLabel: currentRow.versionLabel,
        versionId: currentRow.versionId,
        selectedVersionId: undefined,
        currentMetrics: currentRow.metrics,
        canRemove: false,
      },
      ...comparisonRowStates.map((rowState, index) => ({
        id: rowState.id,
        isCurrent: false,
        versionLabel: '',
        versionId: undefined,
        selectedVersionId: rowState.selectedVersionId,
        currentMetrics: [],
        canRemove: index >= MIN_ROWS_FOR_DELETE,
      })),
    ],
    [currentRow, comparisonRowStates]
  );

  const columns = useMemo(
    () =>
      getCompareEstimatesTableSettings({
        userId,
        versionOptions,
        selectedVersionIds,
        onSelectVersion,
        onRemoveRow,
        selectControlStyles,
        selectStyles: SELECT_STYLES,
      }),
    [userId, versionOptions, selectedVersionIds, onSelectVersion, onRemoveRow]
  );

  return (
    <Modal
      headerText="Compare first year estimates"
      onCloseCallback={onClose}
      showCloseButton
      primaryAction={{ onClick: onClose }}
      modalClassName="!w-[calc(100vw-40px)] !max-w-[1297px] !min-w-[900px] !p-5"
      footer={
        <Button
          type="button"
          variant="brand-filled"
          onClick={onClose}
          data-testid="compare-estimates-done-button"
        >
          Done
        </Button>
      }
      data-testid="compare-estimates-modal"
    >
      <Box sx={{ mt: 2, width: '100%', overflowX: 'auto', minHeight: '131px' }}>
        <TableComponent
          columns={columns}
          data={tableData}
          isLoading={false}
          pagination={false}
          customHeight="max-h-[50vh]"
          ariaLabel="First year estimate comparison"
        />
      </Box>

      {canAddRow && (
        // Kept as a sibling of the overflow-x-auto table wrapper (not nested
        // inside it): setting `overflow-x` to a value other than `visible`
        // makes the browser compute `overflow-y` as `auto` too, turning that
        // div into a clipping box on both axes — which would cut off this
        // button's focus ring even though it never needs to scroll with the
        // table.
        <Box sx={{ mt: 1 }}>
          <Button
            type="button"
            variant="brand-text"
            size="sm"
            iconBefore={<Icons.Plus />}
            onClick={onAddRow}
            data-testid="compare-add-version-button"
          >
            Add version
          </Button>
        </Box>
      )}
    </Modal>
  );
};

export default CompareEstimatesModal;
